import { DeprovisionReason } from '../deprovision-reason.enum';

/**
 * Status codes that can appear in IMPORT_READY exports.
 * These reflect the current device state as-is from Google's API.
 */
export const EXPORT_STATUS_VALUES = [
  'ACTIVE',
  'DISABLED',
  'PRE_PROVISIONED',
  'PRE_PROVISIONED_DISABLED',
  'DELINQUENT',
  'INACTIVE',
  'SHIPPED',
  'RETURN_REQUESTED',
  'RETURN_ARRIVED',
  'Deprovisioned',
  'Unknown',
] as const;

export type ExportStatusValue = (typeof EXPORT_STATUS_VALUES)[number];

/**
 * Display-form device states whose status cannot be changed via import.
 * Annotatable fields (notes, annotatedUser, etc.) can still be updated.
 * DEPROVISIONED is irreversible in Google's API; UNKNOWN is indeterminate.
 */
export const TERMINAL_STATUS_VALUES: readonly string[] = ['Deprovisioned', 'Unknown'];

/**
 * Status codes a user can set via import (including compound deprovision codes).
 * These are the only valid values a user can type into the status column.
 */
export const IMPORT_STATUS_CODES = [
  'ACTIVE',
  'DISABLED',
  'DEPROVISION:REPLACED',
  'DEPROVISION:REPAIR_CENTER',
  'DEPROVISION:NOT_REQUIRED',
  'DEPROVISION:RETIRED',
] as const;

export type ImportStatusCode = (typeof IMPORT_STATUS_CODES)[number];

/**
 * Maps the short deprovision suffix in compound import codes to the
 * Google Workspace API DeprovisionReason enum value.
 */
export const DEPROVISION_CODE_MAP: Record<string, DeprovisionReason> = {
  REPLACED: DeprovisionReason.SAME_MODEL_REPLACEMENT,
  REPAIR_CENTER: DeprovisionReason.REPAIR_CENTER,
  NOT_REQUIRED: DeprovisionReason.NOT_REQUIRED,
  RETIRED: DeprovisionReason.RETIRING_DEVICE,
};

/**
 * Parses an import status code into the status and optional deprovisionReason
 * needed for the device update payload.
 *
 * @returns `null` if the code is not a recognized import status code.
 *
 * @example
 * parseImportStatusCode('ACTIVE')
 * // => { status: 'ACTIVE' }
 *
 * parseImportStatusCode('DEPROVISION:RETIRED')
 * // => { status: 'DEPROVISIONED', deprovisionReason: 'DEPROVISION_REASON_RETIRING_DEVICE' }
 *
 * parseImportStatusCode('Deprovisioned')
 * // => null (terminal status, not a valid import code)
 */
export function parseImportStatusCode(
  code: string,
): { status: string; deprovisionReason?: string } | null {
  const trimmed = code.trim();

  if (trimmed === 'ACTIVE' || trimmed === 'DISABLED') {
    return { status: trimmed };
  }

  if (trimmed.startsWith('DEPROVISION:')) {
    const suffix = trimmed.substring('DEPROVISION:'.length);
    const reason = DEPROVISION_CODE_MAP[suffix];
    if (!reason) return null;
    return { status: 'DEPROVISIONED', deprovisionReason: reason };
  }

  return null;
}

/**
 * Checks whether a status value has an immutable status (cannot be changed via import).
 */
export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUS_VALUES.includes(status.trim());
}

/**
 * Checks whether a value is a valid import status code.
 */
export function isValidImportStatusCode(code: string): boolean {
  return (IMPORT_STATUS_CODES as readonly string[]).includes(code.trim());
}

/**
 * Raw DB-form terminal status values (uppercase, as stored in
 * `vendor_properties->'google'->>'status'`).
 *
 * Distinct from {@link TERMINAL_STATUS_VALUES}, which holds the display values
 * shown in exports.
 */
export const TERMINAL_DB_STATUSES = ['DEPROVISIONED', 'UNKNOWN'] as const;

/**
 * Translates a raw DB status string to its user-facing display value.
 * Used by both the export step and the validate-import diff.
 *
 * - 'DEPROVISIONED' -> 'Deprovisioned'
 * - 'UNKNOWN'      -> 'Unknown'
 * - everything else passes through unchanged ('' for null/undefined).
 */
export function translateRawDbStatus(raw: string | null | undefined): string {
  if (raw == null) return '';
  if (raw === 'DEPROVISIONED') return 'Deprovisioned';
  if (raw === 'UNKNOWN') return 'Unknown';
  return raw;
}

/**
 * Checks whether a raw DB status string represents a status-immutable device state (status cannot be changed via import).
 */
export function isTerminalDbStatus(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  return (TERMINAL_DB_STATUSES as readonly string[]).includes(raw);
}

/**
 * Formats an import status code (what the user typed in the CSV) into a
 * human-readable display string for diff entries.
 *
 * - 'ACTIVE'                  -> 'ACTIVE'
 * - 'DISABLED'                -> 'DISABLED'
 * - 'DEPROVISION:RETIRED'     -> 'Deprovisioned (Retired)'
 * - 'DEPROVISION:REPAIR_CENTER' -> 'Deprovisioned (Repair center)'
 *
 * Returns the input unchanged if not a recognized import code.
 */
export function formatImportStatusCodeForDisplay(code: string): string {
  const trimmed = code.trim();

  if (trimmed === 'ACTIVE' || trimmed === 'DISABLED') {
    return trimmed;
  }

  if (trimmed.startsWith('DEPROVISION:')) {
    const suffix = trimmed.substring('DEPROVISION:'.length);
    if (!suffix) return trimmed;
    const reasonDisplay = suffix
      .toLowerCase()
      .split('_')
      .map((part, idx) => (idx === 0 ? part[0].toUpperCase() + part.slice(1) : part))
      .join(' ');
    return `Deprovisioned (${reasonDisplay})`;
  }

  return trimmed;
}
